import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect
} from "react";
import styles from "css_modules/Select.module.css";
import clsx from "clsx";
import { ReactComponent as Chevron } from "assets/svg/2_chevron_down.svg";

export const MultiSelect = ({
  blank,
  field,
  form,
  displayAttribute,
  inputClass = "",
  data = [],
  value: externalValue,
  onChange,
  withBorder,
  dropUp = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [visibleCount, setVisibleCount] = useState(Infinity);
  const [searchTerm, setSearchTerm] = useState("");

  const containerRef = useRef(null);
  const valueContainerRef = useRef(null);
  const optionsRef = useRef(null);
  const searchInputRef = useRef(null);

  const rawValue = field?.value ?? externalValue ?? [];

  const currentValue = Array.isArray(rawValue)
    ? rawValue.map((v) => (typeof v === "object" ? v.id : v))
    : rawValue !== "" && rawValue != null
    ? [typeof rawValue === "object" ? rawValue.id : rawValue]
    : [];

  const emitChange = (val) => {
    const safeValue = Array.isArray(val) ? val : [];

    const event = {
      target: {
        name: field?.name,
        value: safeValue
      }
    };

    field?.onChange?.(event);
    onChange?.(event);

    if (form?.setFieldValue && field?.name) {
      form.setFieldValue(field.name, safeValue);
    }
  };

  const options = [];

  data?.forEach((item) => {
    const id = item?.id ?? item?.slug ?? item;

    const label =
      typeof item === "string"
        ? item
        : item?.[displayAttribute || "title"] ?? blank;

    options.push({
      id,
      label,
      disabled: item?.off === "true"
    });
  });

  const filteredOptions = options.filter((opt) =>
    String(opt.label)
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase())
  );

  const toggleValue = (opt) => {
    if (!opt || opt.disabled) return;

    let newValue;

    if (
      currentValue.some(
        (v) => String(v) === String(opt.id)
      )
    ) {
      newValue = currentValue.filter(
        (v) => String(v) !== String(opt.id)
      );
    } else {
      newValue = [...currentValue, opt.id];
    }

    emitChange(newValue);
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener(
      "mousedown",
      onClickOutside
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        onClickOutside
      );
  }, []);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    } else {
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  const selectedOptions = options.filter((o) =>
    currentValue.some(
      (v) => String(v) === String(o.id)
    )
  );

  useLayoutEffect(() => {
    const container = valueContainerRef.current;
    if (!container) return;

    const tags = Array.from(
      container.querySelectorAll("[data-tag]")
    );

    if (!tags.length) {
      setVisibleCount(0);
      return;
    }

    const firstTop = tags[0].offsetTop;

    let lastVisible = tags.length;

    for (let i = 0; i < tags.length; i++) {
      if (tags[i].offsetTop > firstTop) {
        lastVisible = i;
        break;
      }
    }

    setVisibleCount(lastVisible);
  }, [selectedOptions]);

  useEffect(() => {
    const container = valueContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      setVisibleCount(Infinity);
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!optionsRef.current || !isOpen) return;

    const el = optionsRef.current;

    const hasScrollbar =
      el.scrollHeight > el.clientHeight;

    el.classList.toggle(
      styles.hasScrollbar,
      hasScrollbar
    );
  }, [filteredOptions.length, isOpen]);

  const visibleTags = selectedOptions.slice(
    0,
    visibleCount
  );

  const hiddenCount =
    selectedOptions.length - visibleCount;

  const hiddenOptions =
    selectedOptions.slice(visibleCount);

  const hiddenLabels = hiddenOptions
    .map((o) => o.label)
    .join(", ");

  return (
    <div
      ref={containerRef}
      className={clsx(
        styles.selectContainer,
        withBorder && styles.withBorder,
        inputClass
      )}
      tabIndex={0}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-controls="multi-select-listbox"
      onMouseDown={(e) => {
        if (
          searchInputRef.current &&
          searchInputRef.current.contains(e.target)
        ) {
          return;
        }

        e.preventDefault();
        setIsOpen((prev) => !prev);
      }}
    >
      <div
        ref={valueContainerRef}
        className={styles.selectedValue}
      >
        {selectedOptions.length === 0 &&
          (blank || "---")}

        {visibleTags.map((opt, i) => (
          <span
            key={opt.id}
            data-tag
            className={styles.multiTag}
            onMouseDown={(e) =>
              e.stopPropagation()
            }
          >
            {opt.label}
            {i < visibleTags.length - 1 &&
              ", "}
          </span>
        ))}

        {hiddenCount > 0 && (
          <span
            className={styles.moreTag}
            title={hiddenLabels}
          >
            +{hiddenCount}
          </span>
        )}
      </div>

      <span
        className={`${styles.arrow} ${
          isOpen ? styles.open : ""
        }`}
      >
        <Chevron />
      </span>

      <div
        ref={optionsRef}
        className={`${styles.options} ${
          isOpen ? styles.open : ""
        } ${
          dropUp ? styles.dropup : ""
        }`}
        onMouseDown={(e) =>
          e.stopPropagation()
        }
      >
        {isOpen && (
          <div
            style={{
              padding: "8px"
            }}
          >
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              placeholder="Hledat..."
              onChange={(e) =>
                setSearchTerm(
                  e.target.value
                )
              }
              onMouseDown={(e) =>
                e.stopPropagation()
              }
              style={{
                width: "100%"
              }}
            />
          </div>
        )}

        

        {filteredOptions.map((opt, idx) => {
          const selected =
            currentValue.some(
              (v) =>
                String(v) ===
                String(opt.id)
            );

          return (
            <div
              key={`${opt.id}-${idx}`}
              className={`${styles.option}
                ${
                  idx ===
                  highlightedIndex
                    ? styles.highlighted
                    : ""
                }
                ${
                  selected
                    ? styles.selected
                    : ""
                }
                ${
                  opt.disabled
                    ? styles.disabled
                    : ""
                }`}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleValue(opt);
              }}
              onMouseEnter={() =>
                setHighlightedIndex(idx)
              }
            >
              {!opt.disabled && (
                <input
                  type="checkbox"
                  readOnly
                  checked={selected}
                  className={
                    styles.checkbox
                  }
                />
              )}

              {opt.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};